import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import Button from '@material-ui/core/Button';
import { useShowAlert } from 'hooks';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import HiringAuthorityForm from 'UI/components/organisms/HiringAuthorityForm';
import HiringAuthoritySelectForm from 'UI/components/organisms/HiringAuthoritySelectForm';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { getId } from 'UI/utils';
import { formatKeywordTags } from 'UI/utils/inventory';

import { HIRING_AUTHORITY_STATUS_ID_MAP } from '../HiringAuthorityForm/utils';
import {
  formatPhoneNumbers,
  getPhonesSortedByDefaultPhone,
  PHONE_FORMAT_MODES
} from '../PhoneNumbersForm/utils';

/**
 * @typedef {Object} HiringAuthorityData
 * @property {number} [id]
 * @property {string} [first_name]
 * @property {string} [last_name]
 * @property {string|Object} [full_name]
 * @property {string} [title]
 * @property {string} [work_email]
 * @property {string} [personal_email]
 * @property {string} [work_phone]
 * @property {string} [personal_phone]
 * @property {string} [ext]
 * @property {string} [other_ext]
 * @property {number} [company_id]
 * @property {number} [position_id]
 * @property {number} [specialty_id]
 * @property {number} [hiring_authority_status_id]
 * @property {Object} [specialty]
 * @property {Object} [position]
 * @property {Object} [subspecialty]
 * @property {Array} [phones]
 * @property {string} [created_at]
 * @property {string} [updated_at]
 */

/**
 * @param {Object} props
 * @param {HiringAuthorityData} [props.hiringAuthority]
 * @param {string} [props.endpoint]
 * @param {number} props.companyId
 * @param {number} props.joborderId
 * @param {Function} [props.onHACompleted]
 * @param {Function} [props.onHAClosed]
 * @param {('company'|'joborder')} props.type
 * @param {boolean} [props.isEditing=false]
 */
const HiringAuthorityDrawer = ({
  hiringAuthority = {},
  onHACompleted,
  onHAClosed,
  endpoint,
  companyId,
  joborderId,
  type,
  isEditing = false
}) => {
  const { showAlert } = useShowAlert();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isMultiplePhonesEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.HiringAuthoritiesMultiplePhones
  );

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isFormDisabled: false,
    isReadOnly: false,
    isFetching: false
  });

  const {
    company_id,
    created_at,
    ext,
    first_name,
    full_name,
    hiring_authority_status_id,
    id,
    last_name,
    other_ext,
    personal_email,
    personal_phone,
    position_id,
    specialty_id,
    hiringAuthorityStatus,
    specialty,
    position,
    subspecialty,
    title,
    updated_at,
    work_email,
    work_phone,
    phones
  } = hiringAuthority || {};

  const currentHiringAuthorityStatus =
    hiringAuthorityStatus || HIRING_AUTHORITY_STATUS_ID_MAP[hiring_authority_status_id];

  const initialValuesHA = {
    specialty_id: specialty,
    subspecialty_id: subspecialty,
    position_id: position,
    hiring_authority_status_id: currentHiringAuthorityStatus
  };

  const defaultPhones = endpoint
    ? getPhonesSortedByDefaultPhone(hiringAuthority, EntityType.HiringAuthority)
    : phones;

  const defaultValues = hiringAuthority?.id
    ? {
        defaultValues: {
          ...hiringAuthority,
          company_id,
          created_at,
          ext,
          first_name,
          full_name,
          hiring_authority_status_id,
          id,
          last_name,
          other_ext,
          personal_email,
          personal_phone,
          position_id,
          specialty_id,
          subspecialty_id: subspecialty?.id,
          title,
          updated_at,
          work_email,
          work_phone,
          phones: defaultPhones
        }
      }
    : {};
  const [isSelectingHA, setIsSelectingHA] = useState(!isEditing);

  const completeForm = useForm(defaultValues);
  const selectForm = useForm();
  const form = isSelectingHA ? selectForm : completeForm;
  const { handleSubmit, reset } = form;

  const enableSaveButton = isEditing || !isSelectingHA || !uiState.isFetching;

  const handleCancelClick = () => {
    onHAClosed && onHAClosed();
    reset();
  };

  const toggleIsFetching = () => setUiState(prev => ({ ...prev, isFetching: !prev.isFetching }));

  const onSubmit = async formData => {
    const isExistingName = formData.isContact;
    const phonesFormatted = formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit);
    const keywordTags = formatKeywordTags(formData.keywordTags);

    if (endpoint) {
      const data = {
        ...formData,
        phones: formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit),
        keywordTags
      };
      if (isMultiplePhonesEnabled) data.work_phone = formData.phone;
      await saveHiringAuthority(omit(data, ['existingHiringAuthority']));
    } else {
      const { existingHiringAuthority, ...formRest } = formData;
      const formWithoutNulls = omitBy(formRest, isNil);
      /* When an HA was selected but had no specialty, it's necessary to merge specialty/subspecialty combos with the existing HA */
      const entityType = isExistingName ? EntityType.Contact : EntityType.HiringAuthority;
      const existingNameData = {
        ...existingHiringAuthority,
        ...formWithoutNulls,
        phones: phonesFormatted,
        previewPhones: getPhonesSortedByDefaultPhone(formData.existingHiringAuthority, entityType)
      };
      const newHiringAuthorityData = {
        ...formData,
        phones: phonesFormatted,
        itemId: getId()
      };
      if (isMultiplePhonesEnabled) {
        newHiringAuthorityData.work_phone = formData.phone;
        if (isExistingName) existingNameData.work_phone = formData.phone;
      }
      onHACompleted &&
        onHACompleted(existingHiringAuthority ? existingNameData : newHiringAuthorityData);
    }
  };

  const hiringAuthorityEditionMode = isEditing
    ? strings.inventoryProfiles.common.editionModes.update
    : strings.inventoryProfiles.common.editionModes.add;

  const saveHiringAuthority = async ha => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      let response;

      if (isSelectingHA) {
        response = ha.isContact
          ? await API.post(endpoint, {
              ...ha,
              company_id: companyId
            })
          : await API.put(
              `${endpoint}/${ha.hiring_authority_id}${
                type === EntityType.Company ? '/assign' : ''
              }`,
              {
                ...ha,
                company_id: companyId
              }
            );
      } else {
        response = isEditing
          ? await API.put(endpoint, { ...ha, company_id: companyId })
          : await API.post(endpoint, { ...ha, company_id: companyId });
      }

      setUiState(prevState => ({
        ...prevState,
        isSuccess: true,
        isSaving: false
      }));

      showAlert({
        severity: 'success',
        title: strings.formatString(
          strings.inventoryProfiles.sections.tabs.hiringAuthorities.update,
          { hiringAuthorityEditionMode }
        )
      });

      onHACompleted && response.data && onHACompleted(response.data);
    } catch (error) {
      setUiState(prevState => ({
        ...prevState,
        isSuccess: false,
        isSaving: false
      }));
      showAlert({
        severity: 'error',
        title: `Hiring authority ${isEditing ? 'not updated' : 'not added'} `,
        error
      });
    }

    setUiState(prevState => ({
      ...prevState,
      isSuccess: false,
      isSaving: false
    }));
  };

  const titleDrawer = isEditing
    ? strings.inventoryProfiles.common.editProfile
    : strings.inventoryProfiles.sections.tabs.hiringAuthorities.addTitle;

  const isFromProfile = !!endpoint;

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: handleCancelClick,
    isSaveDisabled: !enableSaveButton,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, onClose: handleCancelClick }}
      CardHeaderProps={{
        title: titleDrawer,
        subheader:
          isEditing && hiringAuthority?.full_name
            ? hiringAuthority.full_name?.full_name || hiringAuthority.full_name
            : null
      }}
      onClose={handleCancelClick}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      {isSelectingHA && !isEditing ? (
        <FormContext {...form}>
          <span>
            {strings.hiringAuthorities.form.existingHiringAuthority}
            <Button color="primary" onClick={() => setIsSelectingHA(false)}>
              Create a new one
            </Button>
          </span>
          <HiringAuthoritySelectForm
            isFetching={uiState.isFetching}
            isFromProfile={isFromProfile}
            joborderId={joborderId}
            toggleIsFetching={toggleIsFetching}
            type={type}
          />
        </FormContext>
      ) : (
        <FormContext {...form}>
          {!isSelectingHA && !isEditing && (
            <>
              <span>
                {strings.hiringAuthorities.form.newHiringAuthority}
                <Button color="primary" onClick={() => setIsSelectingHA(true)}>
                  {type === EntityType.Company
                    ? strings.hiringAuthorities.form.addedContacts
                    : strings.hiringAuthorities.form.addedCompany}
                </Button>
                instead.
              </span>
              {type === EntityType.Joborder && (
                <p>{strings.hiringAuthorities.form.addedToCompany}</p>
              )}
            </>
          )}
          <HiringAuthorityForm
            initialValues={initialValuesHA}
            isStatusSelectionEnabled={isFromProfile && isEditing}
            isFromProfile={isFromProfile}
            defaultPhones={defaultPhones}
          />
        </FormContext>
      )}
    </FPDrawer>
  );
};

export default HiringAuthorityDrawer;
